import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios'
import { apiErrorText } from './api-error-text'
import { API_BASE } from './config'
import { i18n } from './i18n'
import { tokenStorage } from './token-storage'
import {
  isPublicAuthPath, isPublicAuthUrl, isSessionExpired, logoutNoticeFor, requestRelogin, saveLogoutNotice, waitForSession,
} from './session-expiry'

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
})

/**
 * Request BẮT ĐẦU luồng OAuth (Facebook, Zalo OA, Evotech, TikTok Shop, Shopee) PHẢI gửi kèm credentials.
 * Máy chủ đặt cookie HttpOnly `*_oauth_nonce` gắn `state` với trình duyệt đã bắt đầu luồng (vbsec H6/H7, backend shared/oauth-state-cookie.ts)
 * rồi callback kiểm cookie đó. Web và API khác origin (app.ecdp.vn ↔ api.app.ecdp.vn) → không có `withCredentials`, trình duyệt BỎ QUA
 * Set-Cookie của phản hồi → callback luôn `state_mismatch` (lỗi 14/10/2026 trên dev + production). CORS đã bật `credentials: true` với danh sách origin cụ thể.
 */
export const OAUTH_START_CONFIG = { withCredentials: true } as const

export const getToken = () => tokenStorage.getToken()
export const getRefreshToken = () => tokenStorage.getRefreshToken()
/**
 * Lưu token. `remember`: true = localStorage ("Duy trì đăng nhập"), false = sessionStorage (mất khi đóng trình duyệt),
 * bỏ trống = giữ nguyên nơi đang lưu (làm mới token). Xem token-storage.ts.
 */
export function setTokens(token: string, refreshToken?: string, remember?: boolean) {
  tokenStorage.setTokens(token, refreshToken, remember)
}
export function clearTokens() {
  tokenStorage.clearTokens()
}
/**
 * Bỏ refresh token nhưng giữ access token.
 *
 * Dùng khi xem dưới quyền nhân viên: nếu vẫn còn refresh token của quản trị
 * viên, một lỗi 401 bất kỳ sẽ khiến interceptor lặng lẽ cấp lại token QUẢN TRỊ
 * trong khi giao diện vẫn hiển thị đang là nhân viên — sai quyền mà không ai biết.
 */
export function clearRefreshToken() {
  tokenStorage.clearRefreshToken()
}

// ── Gắn Bearer token ──────────────────────────────────────────────
// Đang chờ đăng nhập lại (hộp "Phiên đã hết hạn") → giữ request tới khi có phiên mới, rồi gắn token mới.
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  if (isSessionExpired() && !isPublicAuthUrl(config.url || '')) await waitForSession()
  const token = getToken()
  if (token) config.headers.Authorization = `Bearer ${token}`
  // Ngôn ngữ giao diện — để backend (sau này) trả thông báo lỗi đúng ngôn ngữ.
  if (i18n.language) config.headers['Accept-Language'] = i18n.language
  return config
})

// ── Auto-refresh khi 401 (gộp các request đồng thời) ──────────────
let isRefreshing = false
let waiters: Array<(token: string | null) => void> = []

/**
 * Báo cho hộp đen góp ý (client-diagnostics.ts) khi LÀM MỚI PHIÊN bị máy chủ từ chối / lỗi — lời gọi này dùng axios trần nên không đi qua `api`,
 * còn hộp đen cố ý bỏ qua 401. Thiếu dấu vết này, các góp ý "đang dùng thì bị đăng xuất" không có gì để tra (góp ý 12/10/2026).
 * Chỉ truyền MÃ lý do của máy chủ (TOKEN_REUSE_DETECTED, SESSION_REVOKED…) — không có token / nội dung.
 */
let refreshFailureListener: ((status: number, code?: string) => void) | null = null
export function setRefreshFailureListener(fn: ((status: number, code?: string) => void) | null) { refreshFailureListener = fn }
function reportRefreshFailure(e: unknown) {
  const r = (e as AxiosError<{ code?: string }> | undefined)?.response
  try { refreshFailureListener?.(r?.status ?? 0, typeof r?.data?.code === 'string' ? r.data.code : undefined) } catch { /* không để hộp đen làm hỏng đăng nhập */ }
}

const REFRESH_TIMEOUT_MS = 8_000
const REFRESH_RETRY_DELAY_MS = 500

/**
 * POST /auth/refresh. Lỗi MẠNG / hết giờ / 5xx → thử lại MỘT lần sau 0,5 giây; 4xx (401 phiên bị thu hồi, 429…) → không thử lại.
 * Lý do (góp ý 12/10/2026 "đang dùng thì bị đăng xuất, wifi vẫn bình thường"): nếu máy chủ ĐÃ xoay token nhưng phản hồi bị mất, trình duyệt vẫn giữ
 * token cũ → lần làm mới sau (quá 10 giây) bị coi là dùng lại token → thu hồi cả phiên → đăng xuất. Thử lại ngay trong "khoảng dung sai"
 * ROTATION_GRACE_MS (10 giây, token-service.ts) thì máy chủ trả lại đúng token kế tiếp (idempotent) → phiên được giữ. Hạn 8 giây/lần để lần thử lại vẫn nằm trong khoảng đó.
 */
export async function postRefresh(refreshToken: string, retryDelayMs = REFRESH_RETRY_DELAY_MS) {
  const call = () => axios.post(`${API_BASE}/auth/refresh`, { refreshToken }, { timeout: REFRESH_TIMEOUT_MS })
  try {
    return await call()
  } catch (e) {
    const status = (e as AxiosError).response?.status
    if (status !== undefined && status < 500) throw e
    await new Promise((resolve) => setTimeout(resolve, retryDelayMs))
    return call()
  }
}

function onRefreshed(token: string | null) {
  waiters.forEach((cb) => cb(token))
  waiters = []
}

/**
 * Phiên không còn dùng được (refresh bị từ chối / token hỏng). Đang trong hệ thống → hiện hộp đăng nhập lại ngay
 * trên trang hiện tại (giữ nguyên màn hình, request tạm dừng). Không có hộp (trang công khai, lúc khởi động) → /login.
 */
export function forceLogout() {
  clearTokens()
  if (requestRelogin()) return
  if (!isPublicAuthPath(window.location.pathname)) {
    window.location.href = '/login'
  }
}

/** Đăng xuất hẳn (không hỏi đăng nhập lại) — kèm thông báo hiện trên trang đăng nhập nếu có. */
export function hardLogout(notice?: string | null) {
  clearTokens()
  if (notice) saveLogoutNotice(notice)
  if (!isPublicAuthPath(window.location.pathname)) {
    window.location.href = '/login'
  }
}

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean }

/** Sau forceLogout: nếu hộp đăng nhập lại đang mở → chờ đăng nhập lại rồi gửi lại request; không thì trả lỗi. */
function retryAfterRelogin(original: RetriableConfig, error: unknown) {
  if (!original || !isSessionExpired()) return Promise.reject(error)
  original._retry = true
  return waitForSession().then(() => api(original))
}

/**
 * Làm mới access token bằng refresh token (dùng chung khoá "đang refresh" với interceptor).
 * Trả token mới, hoặc null khi không có refresh token / refresh bị từ chối (phiên đã bị thu hồi).
 * Socket dùng khi bị máy chủ ngắt (C5).
 */
export async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return null
  if (isRefreshing) {
    return new Promise((resolve) => waiters.push(resolve))
  }
  isRefreshing = true
  try {
    const { data } = await postRefresh(refreshToken)
    setTokens(data.token, data.refreshToken)
    onRefreshed(data.token)
    return data.token as string
  } catch (e) {
    reportRefreshFailure(e)
    onRefreshed(null)
    return null
  } finally {
    isRefreshing = false
  }
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig
    const status = error.response?.status
    const code = (error.response?.data as { code?: string } | undefined)?.code

    // Không thử refresh cho chính endpoint login/refresh
    const url = original?.url || ''
    // Trang công khai (đăng ký / Google / quên mật khẩu): 401 là lỗi nghiệp vụ, không phải hết phiên.
    const isAuthCall = isPublicAuthUrl(url)

    if (status === 401 && !original?._retry && !isAuthCall) {
      const refreshToken = getRefreshToken()
      if (!refreshToken || code === 'TOKEN_INVALID') {
        forceLogout()
        return retryAfterRelogin(original, error)
      }
      original._retry = true

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          waiters.push((token) => {
            if (!token) return retryAfterRelogin(original, error).then(resolve, reject)
            original.headers.Authorization = `Bearer ${token}`
            resolve(api(original))
          })
        })
      }

      isRefreshing = true
      try {
        const { data } = await postRefresh(refreshToken)
        setTokens(data.token, data.refreshToken)
        onRefreshed(data.token)
        original.headers.Authorization = `Bearer ${data.token}`
        return api(original)
      } catch (e) {
        // L2: chỉ đăng xuất khi server từ chối phiên (401/403). Quá tải (429), lỗi mạng, 5xx → giữ phiên,
        // trả lỗi cho request hiện tại; lần gọi sau sẽ thử làm mới lại.
        reportRefreshFailure(e)
        const st = (e as AxiosError).response?.status
        const rejected = st === 401 || st === 403
        // Mở hộp đăng nhập lại TRƯỚC khi báo cho các request đang chờ, để chúng cũng được giữ lại và gửi lại.
        if (rejected) forceLogout()
        onRefreshed(null)
        if (rejected) return retryAfterRelogin(original, e)
        return Promise.reject(e)
      } finally {
        isRefreshing = false
      }
    }

    // Org hết hạn / khóa → đăng xuất hẳn (không hỏi đăng nhập lại), kèm lý do trên trang đăng nhập.
    if (status === 403 && (code === 'ORG_EXPIRED' || code === 'ORG_SUSPENDED')) {
      hardLogout(logoutNoticeFor(code, (error.response?.data as { error?: string } | undefined)?.error))
    }

    return Promise.reject(error)
  },
)

/** Trích thông điệp lỗi thân thiện từ response backend. */
export function apiError(err: unknown): string {
  const e = err as AxiosError<{ error?: string | { message?: string }; message?: string }>
  const body = e?.response?.data
  // Một số API trả { error: { code, message } } — đọc message thay vì hiện "[object Object]".
  const nested = body?.error && typeof body.error === 'object' ? body.error.message : undefined
  // `error` chỉ là tên mã HTTP ("Unauthorized" — lỗi mặc định của Fastify) → apiErrorText lấy `message` tiếng Việt.
  return (
    nested ||
    apiErrorText(body) ||
    e?.message ||
    i18n.t('sharedMisc:apiClient.genericError')
  )
}

/** Trích mã lỗi máy đọc được (`code`) từ response backend — vd `QUOTA_PERSONAL_CHANNELS_EXCEEDED`, `ORG_EXPIRED`. */
export function apiErrorCode(err: unknown): string | null {
  const e = err as AxiosError<{ error?: string | { code?: string }; code?: string }>
  const body = e?.response?.data
  if (!body) return null
  if (body.error && typeof body.error === 'object' && body.error.code) return body.error.code
  return body.code ?? null
}
